'use client';
import { usePathname } from 'next/navigation';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { ImageTextLink } from '../image-text-link/image-text-link';
import { LivestreamNavLink } from './livestream-nav-link';
import type { NavigationItemUI } from '@/logic/page-config/navigation';
import { useStore } from '@/store/store';
import { getNavigatedToProps } from '@/tracking/amplitude/events/general/navigation-link-clicked';
import {
    EVENT_TYPE,
    getSnowplowEvent,
    getNavigationMenuElementData,
    SNOWPLOW_CONTEXT_MAPPING,
    TOP_NAV_SNOWPLOW_BANNER_PROPS
} from '@/tracking/snowplow/utils';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import { classes } from '@/utils/classes/classes';
import { useBiggestWinPopup } from '@/hooks/use-biggest-win-popup';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { NotificationIcon } from '../notification-icon/notification-icon';
import { useGlobalContext } from '@/store/contexts/global-context';
import { shouldDisplayNewTag } from './utils/rewards-badge';
import { useEffect, useState } from 'react';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import { RewardsHubFeatures } from '@fanduel/rewards-hub-client';
import { getPlatformBridge } from '@/logic/bridges';
import { isXsell } from '@/logic/platform';
import { APP_PLATFORM, SUBDOMAINS } from '@/utils/constants';

import {
    LINK_TYPE_CATEGORY,
    LINK_TYPE_NON_CATEGORY,
    NAVIGATION_ITEM_TYPE_LIVESTREAM
} from '@/utils/constants/navigation';

const TAG_NOTIFICATION = 'notification';

type NavItemProps = {
    index: number;
    navItemData: NavigationItemUI;
};

const getTopNavTrackingProps = (navItemData: NavigationItemUI) => {
    const isCategory = navItemData.linkType === LINK_TYPE_CATEGORY;
    return {
        personalized: navItemData.isPersonalized,
        pinnedCategory: navItemData.pinned,
        linkType: isCategory ? LINK_TYPE_CATEGORY : LINK_TYPE_NON_CATEGORY,
        category: isCategory && navItemData.linkUID ? navItemData.linkUID : undefined
    };
};

export const NavItem = ({ index, navItemData }: NavItemProps) => {
    const currentPath = usePathname(); // TODO: this does not cover all cases
    const handleShowLoading = useStore((s) => s.loading.handleShowLoading);

    const { sendEvent } = useStore((state) => state.tracking);

    const showBiggestWinPopup = useBiggestWinPopup();
    const { isLoggedIn, featureFlags } = useGlobalContext();
    const userId = getCookieValue(COOKIES.USER_ID);

    const [shouldShowNotificationIcon, setShouldShowNotificationIcon] = useState(false);

    const isLivestreamNavItem = navItemData.type?.toLowerCase() === NAVIGATION_ITEM_TYPE_LIVESTREAM;
    const isHomeNavItem = navItemData.linkType === 'homepage';
    const isActive = currentPath === navItemData.pathname;
    const isBiggestWinNavItem = navItemData.name.toLowerCase().includes('biggest win');
    const isRafHubXsellLink = isXsell() && !!navItemData.pathname?.startsWith(`https://${SUBDOMAINS.RAF}`);
    const isRewardsOrPromotions =
        !!navItemData.type && ['rewards', 'promotions'].includes(navItemData.type.toLowerCase());

    useEffect(() => {
        if (!isRewardsOrPromotions) return () => {};

        if (isActive) {
            setShouldShowNotificationIcon(false);
            return () => {};
        }

        setShouldShowNotificationIcon(
            shouldDisplayNewTag(isLoggedIn, !!featureFlags[RewardsHubFeatures.HAS_REWARDS_WIDGET], userId)
        );
    }, [isRewardsOrPromotions, isActive, isLoggedIn, featureFlags, userId]);

    const getNavigationClickSnowplowEvent = () => {
        const navigationMenuContext = {
            contextType: SNOWPLOW_CONTEXT_MAPPING.NAVIGATION_MENU_ELEMENT,
            contextProps: getNavigationMenuElementData(
                navItemData.name,
                index + 1,
                'top_bar',
                'top_nav',
                true,
                navItemData.pinned,
                navItemData.isPersonalized
            )
        };
        return getSnowplowEvent(
            EVENT_TYPE.TOP_NAV_SELECTED,
            { ...TOP_NAV_SNOWPLOW_BANNER_PROPS, bannerAction: 'click' },
            [navigationMenuContext]
        );
    };

    const handleNavigationClick = async (event: React.MouseEvent<HTMLAnchorElement>, shouldShowLoading = true) => {
        if (isActive) {
            event.preventDefault();
            return;
        }

        const opensNativeWebView = isRafHubXsellLink && getPlatformBridge().isAvailableFor(APP_PLATFORM.Xsell);

        const eventProps = getNavigatedToProps({
            module: 'top_bar',
            linkText: navItemData.name,
            linkUrl: navItemData.pathname!,
            ...getTopNavTrackingProps(navItemData)
        });
        const snowplowEventProps = getNavigationClickSnowplowEvent();

        if (shouldShowLoading && !opensNativeWebView) {
            handleShowLoading();
        }
        try {
            await ensureCoreEventsInitialized();
            sendEvent([
                { destination: 'amplitude', eventProps },
                { destination: 'snowplow', eventProps: snowplowEventProps }
            ]);
        } catch {
            sendEvent([{ destination: 'amplitude', eventProps }]);
        }
    };

    const handleLinkClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (isRafHubXsellLink) {
            const bridge = getPlatformBridge();

            if (bridge.isAvailableFor(APP_PLATFORM.Xsell)) {
                event.preventDefault();
                bridge.exec('openSBKWebView', navItemData.pathname!);
                await handleNavigationClick(event);
                return;
            }
        }

        if (isBiggestWinNavItem) {
            event.preventDefault();

            const eventProps = getNavigatedToProps({
                module: 'top_bar',
                linkText: navItemData.name,
                linkUrl: navItemData.pathname!,
                tag: navItemData.isHighlighted ? TAG_NOTIFICATION : undefined,
                ...getTopNavTrackingProps(navItemData)
            });
            const snowplowEventProps = getNavigationClickSnowplowEvent();
            try {
                await ensureCoreEventsInitialized();
                sendEvent([
                    { destination: 'amplitude', eventProps },
                    { destination: 'snowplow', eventProps: snowplowEventProps }
                ]);
            } catch {
                sendEvent([{ destination: 'amplitude', eventProps }]);
            }
            showBiggestWinPopup(navItemData.isHighlighted);
            return;
        }

        await handleNavigationClick(event);
    };

    return (
        <li
            key={index}
            className={classes(
                isActive && 'active',
                isActive && 'disabled',
                navItemData?.isHighlighted && 'is-highlighted',
                isLivestreamNavItem && 'live-nav-button',
                isHomeNavItem && 'home-nav-item'
            )}
            aria-disabled={!isActive}>
            {isLivestreamNavItem ? (
                <LivestreamNavLink
                    pathname={navItemData.pathname!}
                    name={navItemData.name}
                    isLoggedIn={isLoggedIn}
                    onNavigationClick={handleNavigationClick}
                />
            ) : (
                <ImageTextLink
                    src={
                        currentPath === navItemData.pathname
                            ? navItemData.customImageActive.url
                            : navItemData.customImage.url
                    }
                    alt={navItemData.customImage.name}
                    width={17}
                    height={17}
                    text={navItemData.name}
                    href={navItemData.pathname!}
                    useNext={false}
                    onClick={handleLinkClick}
                />
            )}
            {shouldShowNotificationIcon && <NotificationIcon count={1} />}
        </li>
    );
};
